import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

/**
 * Custom hook for panning an individual task rect on the timeline.
 *
 * When the user clicks and drags a task rect, only that task shifts by
 * deltaDays. All other tasks are unaffected. The task's duration is preserved.
 *
 * @param {Object} options
 * @param {Array} options.tasks - Array of task objects from schedule data
 * @param {Function} options.onTaskAdjust - Callback with updated tasks array
 * @returns {Object} { isPanning, panningTaskIndex, handlePanStart }
 */
export const useTaskPan = ({ tasks, onTaskAdjust }) => {
  const [panState, setPanState] = useState({
    isPanning: false,
    panningTaskIndex: null,
    startX: 0,
    startTasks: null,
  })

  // Store callbacks in refs to avoid stale closures
  const tasksRef = useRef(tasks)
  const onTaskAdjustRef = useRef(onTaskAdjust)
  const panStateRef = useRef(panState)

  useEffect(() => {
    tasksRef.current = tasks
    onTaskAdjustRef.current = onTaskAdjust
    panStateRef.current = panState
  }, [tasks, onTaskAdjust, panState])

  const handlePanStart = useCallback((taskIndex, clientX) => {
    const currentTasks = tasksRef.current
    if (!currentTasks || currentTasks.length === 0) return

    setPanState({
      isPanning: true,
      panningTaskIndex: taskIndex,
      startX: clientX,
      startTasks: currentTasks.map(t => ({
        ...t,
        _startDuration: parseIntervalToDays(t.duration),
      })),
    })
  }, [])

  const handlePanMove = useCallback((e) => {
    const state = panStateRef.current
    if (!state.isPanning || state.startTasks === null) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)

    if (deltaDays === 0) return

    const { panningTaskIndex, startTasks } = state

    // Shift only the panned task's start date; all others unchanged
    const updatedTasks = startTasks.map(({ _startDuration, ...task }, idx) => {
      if (idx !== panningTaskIndex) return task

      const originalStart = new Date(task.task_window_start)
      const newStart = new Date(originalStart)
      newStart.setDate(newStart.getDate() + deltaDays)
      return {
        ...task,
        task_window_start: newStart.toISOString(),
        // duration unchanged — pan preserves task length
      }
    })

    if (onTaskAdjustRef.current) {
      onTaskAdjustRef.current(updatedTasks)
    }
  }, [])

  const handlePanEnd = useCallback(() => {
    setPanState({
      isPanning: false,
      panningTaskIndex: null,
      startX: 0,
      startTasks: null,
    })
  }, [])

  // Attach/detach global mouse listeners
  useEffect(() => {
    if (panState.isPanning) {
      document.addEventListener('mousemove', handlePanMove)
      document.addEventListener('mouseup', handlePanEnd)

      return () => {
        document.removeEventListener('mousemove', handlePanMove)
        document.removeEventListener('mouseup', handlePanEnd)
      }
    }
  }, [panState.isPanning, handlePanMove, handlePanEnd])

  return {
    isPanning: panState.isPanning,
    panningTaskIndex: panState.panningTaskIndex,
    handlePanStart,
  }
}
